/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import {isInstitutionNodeData} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramInstitutionHelper";
import {
	getRenderedGenogramNodeHeight,
	getRenderedGenogramNodeWidth,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramNodeSizeScaleHelper";
import {isStickyNoteNodeData} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramStickyNoteHelper";

export const PRIMARY_PERSON_FIELD = "isPrimaryPerson"
export const PRIMARY_PERSON_LABEL = "Primary Person"
export const PRIMARY_PERSON_HIGHLIGHT_COLOR = "rgba(1, 105, 217, 0.68)"
export const PRIMARY_PERSON_HIGHLIGHT_PADDING = 10
export const PRIMARY_PERSON_HIGHLIGHT_RADIUS = 6
export const PRIMARY_PERSON_HIGHLIGHT_LINE_WIDTH = 8
export const PRIMARY_PERSON_LEGEND_HIGHLIGHT_COLOR = "rgba(1, 105, 217, 0.82)"
export const PRIMARY_PERSON_LEGEND_HIGHLIGHT_LINE_WIDTH = 7
export const PRIMARY_PERSON_LEGEND_IMAGE_INSET = 4
export const PRIMARY_PERSON_LEGEND_OUTER_SIZE_DELTA = 0
const ANGULAR_SHAPE_RADIUS_REDUCTION = 6
const PRIMARY_PERSON_LABEL_MARGIN_BUFFER = 2
const PRIMARY_PERSON_ROUNDED_LABEL_MARGIN_EXTENSION = 16

export function isPrimaryPersonEligibleNodeData(nodeData) {
	if(!nodeData){
		return false
	}
	if(isStickyNoteNodeData(nodeData) || isInstitutionNodeData(nodeData)){
		return false
	}
	return !nodeData.isAuxNode
}

export function isPrimaryPersonNodeData(nodeData) {
	return !!nodeData?.[PRIMARY_PERSON_FIELD]
}

export function getPrimaryPersonOutlineRadius({
	shape = "",
	width = 0,
	height = 0,
	offset = PRIMARY_PERSON_HIGHLIGHT_PADDING,
	lineWidth = PRIMARY_PERSON_HIGHLIGHT_LINE_WIDTH,
} = {}) {
	let outerRadius = getPrimaryPersonShapeOuterRadius({shape, width, height})
	return outerRadius + offset + lineWidth / 2
}

export function getPrimaryPersonLabelMarginY(nodeData = {}, baseMarginY = 10) {
	let result = baseMarginY
	let shape = resolvePrimaryPersonNodeShape(nodeData)
	if(isPrimaryPersonNodeData(nodeData)){
		let extraMarginY = PRIMARY_PERSON_ROUNDED_LABEL_MARGIN_EXTENSION
		if(isPrimaryPersonAngularShape(shape)){
			let width = getRenderedGenogramNodeWidth(nodeData)
			let height = getRenderedGenogramNodeHeight(nodeData)
			let outlineRadius = getPrimaryPersonOutlineRadius({shape, width, height})
			let symbolRadius = Math.max(width, height) / 2
			let outlineExtension = outlineRadius - symbolRadius
			extraMarginY = Math.max(0, Math.ceil(outlineExtension + PRIMARY_PERSON_LABEL_MARGIN_BUFFER))
		}
		result += extraMarginY
	}
	return result
}

function getPrimaryPersonShapeOuterRadius({shape = "", width = 0, height = 0} = {}) {
	let radius = Math.max(width, height) / 2
	if(isPrimaryPersonAngularShape(shape)){
		let diagonalRadius = Math.hypot(width / 2, height / 2)
		radius = Math.max(radius, diagonalRadius - ANGULAR_SHAPE_RADIUS_REDUCTION)
	}
	return radius
}

function isPrimaryPersonAngularShape(shape = "") {
	return shape === "rectangle"
		|| shape === "roundrectangle"
		|| shape === "round-rectangle"
		|| shape === "triangle"
		|| shape === "diamond"
}

function resolvePrimaryPersonNodeShape(nodeData = {}) {
	let result = String(nodeData.shape || "")
	if(!result){
		let type = String(nodeData.type || "")
		if(type.startsWith("male") || type.startsWith("rectangle") || type === "nonBinary"){
			result = "rectangle"
		} else if(type.startsWith("female") || type.startsWith("ellipse") || type === "unknownGender"){
			result = "ellipse"
		} else if(type){
			result = type
		}
	}
	return result || "ellipse"
}
